import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  createNotificationTemplate,
  getNotificationTemplates,
  getTemplateNotificationTypes,
  getTemplateOperationCodes,
  getTemplateParameterPalette,
  updateNotificationTemplate,
  type NotificationTemplateQuery,
  changeNotificationTemplateStatus,
  removeNotificationTemplate,
} from '../api/notificationTemplateApi'
import type {
  RecordStatusBody,
  NotificationTemplate,
  NotificationTemplateUpsert,
  PagedResponse,
} from '../types'

export const NOTIFICATION_TEMPLATE_KEY = 'admin-notification-templates'

/**
 * Bildirim Şablonu sunucu-durumu (059): filtreli/sayfalı liste, combo seçenekleri, parametre paneli
 * kataloğu ve oluştur/güncelle mutation'ları.
 *
 * **`i18n.language` queryKey'in parçasıdır:** liste satırlarındaki `operationCodeName`/
 * `notificationTypeName`, combo adları ve panel etiketleri sunucuda `Accept-Language`'e göre çözülür.
 * Dil TR↔EN değiştiğinde anahtar değişmezse önbellekten eski dildeki yanıt sunulur.
 *
 * **`keepPreviousData`:** sunucu-taraflı sayfalamada sayfa değişirken `isLoading` yeniden `true` olur;
 * paylaşılan `DataTable`'ın iskelet dalı paginator'ı render etmediği için sayfalama kontrolü kaybolup
 * tablo titrer. Önceki veri korunarak bu önlenir.
 */
export function useNotificationTemplates(query: NotificationTemplateQuery) {
  const qc = useQueryClient()
  const { i18n } = useTranslation()

  const list = useQuery({
    // `sort` anahtarın parçasıdır: kolon sıralaması sunucu-taraflıdır → değişince yeniden çekilmeli.
    queryKey: [
      NOTIFICATION_TEMPLATE_KEY,
      i18n.language,
      query.operationCodeId ?? null,
      query.notificationTypeId ?? null,
      query.page,
      query.size,
      query.sort ?? null,
    ],
    queryFn: () => getNotificationTemplates(query),
    placeholderData: keepPreviousData,
  })

  const operationCodes = useQuery({
    queryKey: [NOTIFICATION_TEMPLATE_KEY, 'operation-codes', i18n.language],
    queryFn: getTemplateOperationCodes,
  })

  const notificationTypes = useQuery({
    queryKey: [NOTIFICATION_TEMPLATE_KEY, 'notification-types', i18n.language],
    queryFn: getTemplateNotificationTypes,
  })

  /** Parametre paneli kataloğu (FR-021a) — form açılmadan da önbelleğe alınır (panel anında dolu gelir). */
  const parameters = useQuery({
    queryKey: [NOTIFICATION_TEMPLATE_KEY, 'parameters', i18n.language],
    queryFn: getTemplateParameterPalette,
  })

  const invalidateList = () => qc.invalidateQueries({ queryKey: [NOTIFICATION_TEMPLATE_KEY] })

  /**
   * Sunucudan dönen güncel kaydı (yeni `modDate` ile) önbellekteki liste sayfalarına **hemen** yazar
   * — 011 `useTicketTypes` / 015 `useGrants` deseni: "bayat modDate → 409 önlenir".
   *
   * Anahtar **prefix**'le taranır (`setQueriesData`): liste anahtarı dil + iki filtre + sayfa + boyut +
   * sıralama taşır, çağıranın o bileşimi yeniden kurması kırılgan olurdu.
   *
   * **Prefix'e `i18n.language` DAHİL edilir**: DTO'daki `operationCodeName`/`notificationTypeName`
   * sunucuda `Accept-Language`'e göre çözülür, yani kayıt **dile özgüdür**. Yalnız `[KEY]` ile taransaydı
   * aktif dilin kaydı diğer dilin önbelleğine yazılır ve dil değiştirildiğinde tablo kısa süre TR adları
   * EN görünümde gösterirdi (bu dosyanın en başında belgelenen dil-anahtarlı değişmezin ihlali).
   * Dil segmenti ayrıca combo/parametre sorgularını da doğal olarak dışarıda bırakır — onların
   * anahtarında 1. eleman sabit bir etikettir (`'operation-codes'` vb.), dil değil. `content` denetimi
   * yine de savunma olarak durur.
   */
  const applyUpdated = (updated: NotificationTemplate) =>
    qc.setQueriesData<PagedResponse<NotificationTemplate>>(
      { queryKey: [NOTIFICATION_TEMPLATE_KEY, i18n.language] },
      (prev) =>
        prev && Array.isArray(prev.content)
          ? { ...prev, content: prev.content.map((t) => (t.id === updated.id ? updated : t)) }
          : prev,
    )

  /**
   * Yazma sonrası tazeleme. **Bilinçli olarak `invalidateQueries` promise'i DÖNDÜRÜLMEZ** (gövde `{}`
   * içinde): React Query, mutation'ın `onSuccess`'inden dönen promise'i **bekler** ve mutation ancak o
   * zaman "settled" olur. Promise beklenirse mutation, bu anahtar altındaki **tüm** sorguların
   * (liste + iki combo + parametre kataloğu) yeniden çekilmesi bitene kadar açık kalır; biri yavaşlar,
   * 5xx'te yeniden denenir veya hiç dönmezse `mutate(..., { onSettled })` **hiç çalışmaz** → satırın
   * `busyId` kilidi açılmaz ve **switch kalıcı olarak disabled kalır** (kullanıcı ancak sayfayı
   * yenileyerek kurtulur). Kayıt zaten `applyUpdated` ile güncellendiğinden tazeleme arka planda
   * sürebilir.
   */
  const refreshAfterWrite = (updated?: NotificationTemplate) => {
    if (updated) applyUpdated(updated)
    invalidateList()
  }

  const create = useMutation({
    mutationFn: (body: NotificationTemplateUpsert) => createNotificationTemplate(body),
    onSuccess: () => refreshAfterWrite(),
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: number; body: NotificationTemplateUpsert }) =>
      updateNotificationTemplate(id, body),
    onSuccess: (data) => refreshAfterWrite(data),
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RecordStatusBody }) => changeNotificationTemplateStatus(id, body),
    onSuccess: (data) => refreshAfterWrite(data),
  })

  const remove = useMutation({
    mutationFn: (id: number) => removeNotificationTemplate(id),
    onSuccess: () => refreshAfterWrite(),
  })

  return {
    list,
    operationCodes,
    notificationTypes,
    parameters,
    create,
    update,
    changeStatus,
    remove,
  }
}
